import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { createPortal } from "react-dom";
import { Global, css } from "@emotion/react";
import Iconify from "../../../minimals/components/iconify";
import { TABS } from "./TABS";
import { useNavigate, useParams } from "react-router-dom";
import { useSettingsContext } from "../../contexts/SettingsContext";
import { useEffect, useState, useCallback, useMemo, useContext } from "react";
import Contacts from "./Contacts";
import { useImmer } from "use-immer";
import {
  ActiveStatusFilter,
  CompanySearchResults,
  CompanyType,
  ContactSearchResults,
  Role,
  SearchData
} from "../types";
import { debounce, values } from "lodash";
import { alpha } from "@mui/material/styles";
import axios, { CancelToken, CancelTokenSource } from "axios";
import Companies from "./Companies";
import ComingSoon from "../shared/ComingSoon";
import { logEvent } from "../../utils/analyticsLogger";
import { useQueryParam, StringParam } from "use-query-params";
import { downloadExportList, getTodaysExportContactIds, isContactExportViewer } from "../../utils/exportContacts";
import { UserContext } from "../../contexts/UserContext";
import FeedbackButton from "../../shared/FeedbackButton";
import useResponsive from "../../../minimals/hooks/useResponsive";
import { HeaderSlotContext } from "../../components/layouts/dashboard/HeaderSlotContext";
import FeedbackModal from "../../shared/FeedbackModal";

const floatingBarButtonSx = {
  flexShrink: 0,
  width: 40,
  height: 40,
  color: "primary.dark",
  backgroundColor: "background.paper",
  border: "1px solid",
  borderColor: "rgba(145, 158, 171, 0.48)",
  boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)",
  "&:hover": { backgroundColor: "background.paper" }
};

export default function ContactList() {
  const { section } = useParams();
  const { user } = useContext(UserContext);

  const isUserContactExportViewer = useMemo(() => isContactExportViewer(user), [user]);
  const Section = {
    INDIVIDUALS: "individuals",
    DEACTIVATED_INDIVIDUALS: "deactivatedindividuals",
    DEACTIVATED_COMPANIES: "deactivatedcompanies",
    COMPANIES: "companies",
    BLACKLISTED: "blacklisted"
  };

  const [isLoading, setIsLoading] = useState(true);
  // performSearch holds isLoading false for a second to avoid flicker on fast
  // searches. That reads as a frozen list on a phone, so mobile shows the skeleton
  // from the moment the term changes instead.
  const [isSearchPending, setIsSearchPending] = useState(false);
  const { themeStretch } = useSettingsContext();
  const theme = useTheme();
  const isMobile = useResponsive("down", "md");
  const { slotEl } = useContext(HeaderSlotContext);
  // The desktop feedback tab is a fixed, rotated bar down the right edge, which has
  // nowhere to live on a phone. Mobile reaches the same modal from the drawer.
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  // iOS-style edit mode: rows only become selectable once this is on, so the
  // list isn't carrying a checkbox column it rarely needs.
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const toggleSelectionMode = () => {
    setIsSelectionMode((wasOn) => {
      if (wasOn) {
        setSelectedContactIds([]);
        setCompaniesToMerge(new Set());
      }
      return !wasOn;
    });
  };
  const navigate = useNavigate();
  const [contactsSearchResults, setContactsSearchResults] = useState<ContactSearchResults>();
  const [deactivatedContactsSearchResults, setDeactivatedContactsSearchResults] = useState<ContactSearchResults>();
  const [companiesSearchResults, setCompaniesSearchResults] = useState<CompanySearchResults>();
  const [deactivatedCompaniesSearchResults, setDeactivatedCompaniesSearchResults] = useState<CompanySearchResults>();
  const [query, setQuery] = useQueryParam("query", StringParam);
  const contactCompaniesOnlyOption = { id: -1, name: "All Non Vendors" };
  const defaultOption = { id: 0, name: "All" };
  const defaultSelectedRole = (
    localStorage.getItem("selectedRole") ? JSON.parse(localStorage.getItem("selectedRole") || "") : defaultOption
  ) as Role;

  const defaultSelectedCompanyType = (
    localStorage.getItem("selectedCompanyType")
      ? JSON.parse(localStorage.getItem("selectedCompanyType") || "")
      : contactCompaniesOnlyOption
  ) as CompanyType;

  const defaultSearchData = {
    searchTerm: "",
    pageSize: 100,
    pageNumber: 0,
    roleId: -1,
    companyTypeId: -1,
    isContactOnlySearch: defaultSelectedRole?.id === -1,
    isContactCompanyOnlySearch: defaultSelectedCompanyType?.id === -1,
    activeStatus: ActiveStatusFilter.ActiveOnly
  } as SearchData;

  const [searchData, setSearchData] = useImmer<SearchData>({
    ...defaultSearchData,
    searchTerm: query || "",
    roleId: defaultSelectedRole?.id || null,
    companyTypeId: defaultSelectedCompanyType?.id || null
  });

  const contactOnlyOption = { id: -1, name: "All Clients" };
  const [roleList, setRoleList] = useState<Role[]>([contactOnlyOption, defaultOption]);
  const [companyTypeList, setCompanyTypeList] = useState<CompanyType[]>([contactCompaniesOnlyOption, defaultOption]);
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [contactToExportCount, setContactToExportCount] = useState<number>(0);
  const [companiesToMerge, setCompaniesToMerge] = useState<Set<number>>(new Set());
  const MAX_COMPANIES_TO_MERGE = 3;

  const getAndSetRoles = async () => {
    const { data } = await axios.get(`/api/clientphonebook/roles/search`);
    setRoleList((prev) => [...prev, ...data]);
  };

  const getAndSetCompanyTypes = async () => {
    const { data } = await axios.get(`/api/clientphonebook/companies/getAllTypes`);
    setCompanyTypeList((prev) => [...prev, ...data]);
  };

  let cancelTokenSource: CancelTokenSource | undefined;

  const performSearch = async (
    apiEndpoint: string,
    searchData: SearchData,
    searchTerm: string,
    setSearchResults: Function,
    cancelToken: CancelToken
  ) => {
    let loadingTimeout: string | number | NodeJS.Timeout | undefined;

    const startLoadingTimer = () => {
      loadingTimeout = setTimeout(() => {
        setIsLoading(true);
      }, 1000); // Adjust the timeout value to your desired duration
    };

    const clearLoadingTimer = () => {
      clearTimeout(loadingTimeout);
    };

    try {
      setIsLoading(false);
      startLoadingTimer();
      const cleanedSearchTerm = searchTerm.replace(",", "");
      const { data } = await axios.post(
        apiEndpoint,
        {
          ...searchData,
          roleId: searchData.roleId === 0 ? null : searchData.roleId,
          companyTypeId: searchData.companyTypeId === 0 ? null : searchData.companyTypeId,
          searchTerm: cleanedSearchTerm
        },
        {
          cancelToken // Pass the cancellation token to the request
        }
      );

      clearLoadingTimer();

      setSearchResults(data);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Search canceled:", error.message);
      } else {
        console.error(error);
      }
    } finally {
      setIsLoading(false);
      setIsSearchPending(false);
    }
  };

  const searchContactsCommon = async (
    searchTerm: string,
    setData: (data: any) => void,
    additionalData: object = {}
  ) => {
    if (cancelTokenSource) {
      // Cancel the previous search request if it exists
      cancelTokenSource.cancel("Operation canceled by the user.");
    }

    // Create a new cancellation token
    cancelTokenSource = axios.CancelToken.source();

    await performSearch(
      "/api/clientphonebook/contacts/getPaginated",
      { ...searchData, ...additionalData },
      searchTerm,
      setData,
      cancelTokenSource.token
    );
  };

  const searchContacts = async (searchTerm: string) => {
    await searchContactsCommon(searchTerm, setContactsSearchResults);
  };

  const searchDeactivatedContacts = async (searchTerm: string) => {
    await searchContactsCommon(searchTerm, setDeactivatedContactsSearchResults, {
      activeStatus: ActiveStatusFilter.InactiveOnly
    });
  };

  const searchCompaniesCommon = async (
    searchTerm: string,
    setData: (data: any) => void,
    additionalData: object = {}
  ) => {
    if (cancelTokenSource) {
      // Cancel the previous search request if it exists
      cancelTokenSource.cancel("Operation canceled by the user.");
    }

    // Create a new cancellation token
    cancelTokenSource = axios.CancelToken.source();

    await performSearch(
      "/api/clientphonebook/companies/getPaginated",
      { ...searchData, ...additionalData },
      searchTerm,
      setData,
      cancelTokenSource.token
    );
  };

  const searchCompanies = async (searchTerm: string) => {
    await searchCompaniesCommon(searchTerm, setCompaniesSearchResults);
  };

  const searchDeactivatedCompanies = async (searchTerm: string) => {
    await searchCompaniesCommon(searchTerm, setDeactivatedCompaniesSearchResults, {
      activeStatus: ActiveStatusFilter.InactiveOnly
    });
  };

  const debouncedSearchContacts = useCallback(
    debounce((val) => {
      searchContacts(val);
      setQuery(val, "replaceIn");
      logEvent("Phonebook", "Searched", `Searched ${val}`);
    }, 250),
    [searchData.roleId]
  );

  const handleContactTermChange = (searchTerm: string) => {
    setSearchData((draft) => {
      draft.searchTerm = searchTerm;
    });
    debouncedSearchContacts(searchTerm);
  };

  const debouncedSearchDeactivatedContacts = useCallback(
    debounce((val) => {
      searchDeactivatedContacts(val);
      setQuery(val, "replaceIn");
    }, 250),
    [searchData.roleId]
  );
  const handleDeactivatedContactTermChange = (searchTerm: string) => {
    setSearchData((draft) => {
      draft.searchTerm = searchTerm;
    });
    debouncedSearchDeactivatedContacts(searchTerm);
  };

  const debouncedSearchCompanies = useCallback(
    debounce((val) => {
      searchCompanies(val);
      setQuery(val, "replaceIn");
    }, 250),
    [searchData.companyTypeId]
  );

  const debouncedSearchDeactivatedCompanies = useCallback(
    debounce((val) => {
      searchDeactivatedCompanies(val);
      setQuery(val, "replaceIn");
    }, 250),
    [searchData.companyTypeId]
  );

  const handleCompanyTermChange = (searchTerm: string) => {
    setSearchData((draft) => {
      draft.searchTerm = searchTerm;
    });
    debouncedSearchCompanies(searchTerm);
  };

  const handleDeactivatedCompanyTermChange = (searchTerm: string) => {
    setSearchData((draft) => {
      draft.searchTerm = searchTerm;
    });
    debouncedSearchDeactivatedCompanies(searchTerm);
  };

  const handleSearchTermChange = (searchTerm: string) => {
    setIsSearchPending(true);
    if (section === Section.INDIVIDUALS) {
      handleContactTermChange(searchTerm);
    } else if (section === Section.COMPANIES) {
      handleCompanyTermChange(searchTerm);
    } else if (section === Section.DEACTIVATED_INDIVIDUALS) {
      handleDeactivatedContactTermChange(searchTerm);
    } else if (section === Section.DEACTIVATED_COMPANIES) {
      handleDeactivatedCompanyTermChange(searchTerm);
    }
  };

  const handlePageNumberChange = (number: number) => {
    setSearchData((draft) => {
      draft.pageNumber = number;
    });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchData((draft) => {
      draft.pageSize = parseInt(event.target.value, 10);
      draft.pageNumber = 0;
    });
  };

  const handleRoleChange = (selectedRole: Role) => {
    localStorage.setItem("selectedRole", JSON.stringify(selectedRole));

    setSearchData((draft) => {
      draft.roleId = selectedRole.id;
      draft.isContactOnlySearch = selectedRole.id == -1;
    });
  };

  const handleCompanyTypeChange = (selectedCompanyType: CompanyType) => {
    localStorage.setItem("selectedCompanyType", JSON.stringify(selectedCompanyType));
    setSearchData((draft) => {
      draft.companyTypeId = selectedCompanyType.id;
      draft.isContactCompanyOnlySearch = selectedCompanyType.id == -1;
    });
  };

  // `compact` is the floating-bar version: no label, and the value ellipsises
  // rather than widening the control past what the bar can spare.
  const renderSectionFilter = (compact = false) => {
    if (section === Section.INDIVIDUALS) {
      return (
        <Autocomplete
          defaultValue={defaultSelectedRole}
          fullWidth
          renderInput={(params) =>
            compact ? (
              <TextField {...params} size="small" placeholder="Role" />
            ) : (
              <TextField {...params} placeholder="Search" label="Roles/Professions" />
            )
          }
          onChange={(_, value) => {
            if (!value) return;
            handleRoleChange(value);
          }}
          getOptionLabel={(option) => option.name}
          options={roleList}
        />
      );
    }
    if (section === Section.COMPANIES) {
      return (
        <Autocomplete
          defaultValue={defaultSelectedCompanyType}
          fullWidth
          renderInput={(params) =>
            compact ? (
              <TextField {...params} size="small" placeholder="Type" />
            ) : (
              <TextField {...params} placeholder="Search" label="Company Types" />
            )
          }
          onChange={(_, value) => {
            if (!value) return;
            handleCompanyTypeChange(value);
          }}
          getOptionLabel={(option) => option.name}
          options={companyTypeList}
        />
      );
    }
    return null;
  };

  const renderMergeButton = () => {
    if (section == Section.INDIVIDUALS) {
      return (
        <IconButton
          disabled={selectedContactIds.length !== 2}
          onClick={() => {
            const queryParams = selectedContactIds.map((id) => `ids=${id}`).join("&");
            navigate(`../phonebook/contactsMerge?${queryParams}`);
          }}
        >
          <Iconify icon="lucide:merge" />
        </IconButton>
      );
    }

    if (section == Section.COMPANIES) {
      const baseURL = "/phonebook/companiesMerge";

      const queryParams = Array.from(companiesToMerge)
        .map((id) => `ids=${id}`)
        .join("&");

      return (
        <IconButton
          disabled={companiesToMerge.size <= 1 || companiesToMerge.size > MAX_COMPANIES_TO_MERGE}
          onClick={() => {
            navigate(`${baseURL}?${queryParams}`);
          }}
        >
          <Iconify icon="lucide:merge" />
        </IconButton>
      );
    }
  };

  useEffect(() => {
    if (section === Section.INDIVIDUALS) {
      searchContacts(searchData.searchTerm);
    } else if (section === Section.DEACTIVATED_INDIVIDUALS) {
      searchDeactivatedContacts(searchData.searchTerm);
    } else if (section === Section.DEACTIVATED_COMPANIES) {
      searchDeactivatedCompanies(searchData.searchTerm);
    } else {
      searchCompanies(searchData.searchTerm);
    }
  }, [searchData.pageNumber, searchData.pageSize, searchData.roleId, searchData.companyTypeId, section]);

  useEffect(() => {
    setSearchData((draft) => {
      draft.pageNumber = 0;
    });
  }, [section, searchData.searchTerm, searchData.roleId, searchData.companyTypeId]);

  const getAndSetExportContactCount = async () => {
    const contactIds = await getTodaysExportContactIds();
    setContactToExportCount(contactIds.length);
  };

  const downloadExportListAndResetCount = async () => {
    await downloadExportList();
    getAndSetExportContactCount();
  };

  useEffect(() => {
    getAndSetRoles();
    getAndSetCompanyTypes();
  }, []);

  useEffect(() => {
    if (isUserContactExportViewer) {
      getAndSetExportContactCount();
    }
  }, [isUserContactExportViewer]);

  const searchWords = useMemo(() => searchData.searchTerm.split(" "), [searchData.searchTerm]);
  return (
    <>
      <Helmet>
        <title> Contacts | TitleQ</title>
      </Helmet>

      {isMobile && (
        <Global
          styles={css`
            main {
              /* Mobile drops the in-page heading block, which was supplying the
                 clearance under the fixed 64px header. */
              padding-top: 64px !important;
            }
          `}
        />
      )}

      {/* Same pattern as /calendar: on mobile the heading and its actions render in
          the site header beside the nav button rather than costing a page row. */}
      {isMobile && slotEl
        ? createPortal(
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: 1, minWidth: 0, gap: 1 }}
            >
              <Typography sx={{ color: "primary.dark", fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
                Contacts
              </Typography>
              <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, gap: 1.5 }}>
                <IconButton
                  size="small"
                  aria-label="Submit contact update"
                  onClick={() => setIsFeedbackOpen(true)}
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  {/* Bubble-with-pencil: this submits a suggested change to the
                      phonebook team, it doesn't edit the record directly. */}
                  <Iconify icon="material-symbols:rate-review-outline" width={18} />
                </IconButton>
                {isUserContactExportViewer && (
                  <Badge badgeContent={contactToExportCount} color="primary">
                    <IconButton
                      size="small"
                      aria-label="Download contact list"
                      onClick={() => downloadExportListAndResetCount()}
                      sx={{ border: "1px solid", borderColor: "divider" }}
                    >
                      <Iconify icon="eva:download-fill" width={18} />
                    </IconButton>
                  </Badge>
                )}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Iconify icon="eva:plus-fill" width={16} />}
                  onClick={() => navigate("../phonebook/create-contact")}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  New
                </Button>
              </Stack>
            </Stack>,
            slotEl
          )
        : null}

      <Container maxWidth={themeStretch ? false : "lg"} sx={isMobile ? { pb: 12, px: "0 !important" } : undefined}>
        {!isMobile && (
          <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom={3}>
            <Typography variant="h4">Contacts</Typography>
            <Stack direction="row" spacing={2}>
              {isUserContactExportViewer && (
                <Badge badgeContent={contactToExportCount} color="primary">
                  <Button
                    size="medium"
                    variant="outlined"
                    startIcon={<Iconify icon="eva:download-fill" />}
                    onClick={() => downloadExportListAndResetCount()}
                  >
                    Download Contact List
                  </Button>
                </Badge>
              )}
              <Button
                size="medium"
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() => navigate("../phonebook/create-contact")}
              >
                New
              </Button>
            </Stack>
          </Stack>
        )}
        {/* Mobile drops the card's frame and padding so the list runs edge to edge —
            on a phone the border is a second box around a screen-wide list. */}
        <Card
          sx={
            isMobile
              ? { boxShadow: "none", borderRadius: 0, backgroundColor: "transparent", overflow: "visible" }
              : undefined
          }
        >
          <CardContent
            sx={{
              px: { xs: 0, md: 3 },
              py: { xs: 0, md: 3 },
              "&:last-child": { paddingBottom: { xs: 0, md: 3 } }
            }}
          >
            {!isMobile && (
              <Grid container marginBottom={3}>
                <Grid item md={5} xs={12}>
                  <TextField
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-fill" />
                        </InputAdornment>
                      )
                    }}
                    value={searchData.searchTerm}
                    placeholder="Search"
                    onChange={(e) => handleSearchTermChange(e.target.value)}
                  />
                </Grid>
                <Grid item md={4} xs={12}></Grid>
                <Grid item md={3} xs={12}>
                  {renderSectionFilter()}
                </Grid>
              </Grid>
            )}
            {!isMobile && (
              <FeedbackButton
                url={window.location.href}
                destinationEmail={"phonebook@madisoncres.com"}
                subject="New phonebook update submitted"
                placeholder="Updated contact information"
                buttonText="Submit Contact Update"
              />
            )}
            <Box
              paddingLeft={{ xs: 2, md: 3 }}
              paddingRight={{ xs: 2, md: 2 }}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: theme.palette.background.neutral
              }}
            >
              <Tabs
                value={section}
                onChange={(e, value) => {
                  let queryString = "";
                  if (query) {
                    queryString = `?query=${query}`;
                  }
                  navigate(`../phonebook/contacts/${value}${queryString}`);
                }}
              >
                {TABS.filter((tab) => tab.visible).map((t) => (
                  <Tab label={t.label} value={t.value} key={t.value} />
                ))}
              </Tabs>
              {/* Four icons plus two tabs is a tight fit at 375px, so mobile trims the
                  button padding rather than letting the row wrap. */}
              <Stack direction="row" sx={isMobile ? { "& .MuiIconButton-root": { padding: 0.75 } } : undefined}>
                {isMobile && (
                  <Button
                    size="small"
                    onClick={toggleSelectionMode}
                    sx={{ minWidth: 0, px: 1, fontWeight: isSelectionMode ? 700 : 400 }}
                  >
                    {isSelectionMode ? "Done" : "Select"}
                  </Button>
                )}
                {renderMergeButton()}
                {/* Neither of these has ever had a handler; mobile can't spare the
                    width for buttons that do nothing. */}
                {!isMobile && (
                  <IconButton>
                    <Iconify icon="material-symbols:download" />
                  </IconButton>
                )}
                {!isMobile && (
                  <IconButton onClick={() => null}>
                    <Iconify icon="eva:trash-2-outline" />
                  </IconButton>
                )}
              </Stack>
            </Box>
            <Divider />
            {section === Section.INDIVIDUALS && (
              <Contacts
                selectedContactIds={selectedContactIds}
                setSelectedContactIds={setSelectedContactIds}
                isSelectionMode={isMobile && isSelectionMode}
                contactsSearchResults={contactsSearchResults}
                searchData={searchData}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handlePageNumberChange={handlePageNumberChange}
                searchWords={searchWords}
                isLoading={isLoading || (isMobile && isSearchPending)}
              />
            )}
            {section === Section.COMPANIES && (
              <Companies
                companiesSearchResults={companiesSearchResults}
                searchData={searchData}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handlePageNumberChange={handlePageNumberChange}
                searchWords={searchWords}
                isLoading={isLoading || (isMobile && isSearchPending)}
                companiesToMerge={companiesToMerge}
                setCompaniesToMerge={setCompaniesToMerge}
                maxCompaniesToMerge={MAX_COMPANIES_TO_MERGE}
                isSelectionMode={isMobile && isSelectionMode}
              />
            )}
            {section === Section.DEACTIVATED_INDIVIDUALS && (
              <Contacts
                selectedContactIds={selectedContactIds}
                setSelectedContactIds={setSelectedContactIds}
                isSelectionMode={isMobile && isSelectionMode}
                contactsSearchResults={deactivatedContactsSearchResults}
                searchData={searchData}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handlePageNumberChange={handlePageNumberChange}
                searchWords={searchWords}
                isLoading={isLoading || (isMobile && isSearchPending)}
              />
            )}
            {section === Section.DEACTIVATED_COMPANIES && (
              <Companies
                companiesSearchResults={deactivatedCompaniesSearchResults}
                searchData={searchData}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handlePageNumberChange={handlePageNumberChange}
                searchWords={searchWords}
                isLoading={isLoading || (isMobile && isSearchPending)}
                companiesToMerge={companiesToMerge}
                setCompaniesToMerge={setCompaniesToMerge}
                maxCompaniesToMerge={MAX_COMPANIES_TO_MERGE}
                isSelectionMode={isMobile && isSelectionMode}
              />
            )}
            {section === Section.BLACKLISTED && <ComingSoon />}
          </CardContent>
        </Card>
      </Container>

      {/* Same floating bar as /calendar and /documents: search stays reachable with a
          thumb, and the section filter moves behind a button so it costs no width. */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (theme) => theme.zIndex.drawer - 1,
            px: 1.5,
            pt: 4,
            pb: "calc(12px + env(safe-area-inset-bottom))",
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            // Gradient scrim, not a panel: it fades out with no edge, so rows don't
            // show through the gaps without reading as a second layer.
            background: `linear-gradient(to top, ${theme.palette.background.default} 55%, ${alpha(
              theme.palette.background.default,
              0
            )})`
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ pointerEvents: "auto", width: 1, maxWidth: 520, minWidth: 0 }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 999,
                  backgroundColor: "background.paper",
                  boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)"
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha(theme.palette.grey[500], 0.48)
                }
              }}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Search"
                value={searchData.searchTerm}
                onChange={(e) => handleSearchTermChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" width={20} />
                    </InputAdornment>
                  ),
                  endAdornment: searchData.searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Clear search"
                        onClick={() => handleSearchTermChange("")}
                        edge="end"
                        sx={{ color: "text.disabled" }}
                      >
                        <Iconify icon="eva:close-circle-fill" width={18} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Box>

            {(section === Section.INDIVIDUALS || section === Section.COMPANIES) && (
              <Box
                sx={{
                  width: 156,
                  flexShrink: 0,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 999,
                    backgroundColor: "background.paper",
                    boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)",
                    paddingRight: "32px !important"
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha(theme.palette.grey[500], 0.48)
                  },
                  // Role names run long ("Real Estate Broker (Commercial)"), and an
                  // input clips rather than ellipsising on its own.
                  "& .MuiInputBase-input": { textOverflow: "ellipsis" }
                }}
              >
                {renderSectionFilter(true)}
              </Box>
            )}
          </Stack>
        </Box>
      )}

      {isFeedbackOpen && (
        <FeedbackModal
          onClose={() => setIsFeedbackOpen(false)}
          open={isFeedbackOpen}
          url={window.location.href}
          destinationEmail={"phonebook@madisoncres.com"}
          subject="New phonebook update submitted"
          placeholder="Updated contact information"
        />
      )}
    </>
  );
}
